import { useState } from "react";

/**
 * LogoStrip — replaces text-based trust strip with real client logos.
 * Drop PNG files (transparent BG, ≥400px wide) at /logos/<slug>.png in /app/frontend/public/.
 * If a logo image fails to load, the component gracefully falls back to the elegant text name.
 *
 * Behaviour:
 *   - Greyscale + reduced opacity by default; full colour on hover.
 *   - Single-row layout on desktop; 2-column grid on mobile.
 *   - Aligned heights via fixed-height box; no logo dominates.
 */

const CLIENTS = [
  { name: "Vivanta Bhubaneswar", slug: "vivanta-bhubaneswar" },
  { name: "Trident Bhubaneswar", slug: "trident-bhubaneswar" },
  { name: "Lyfe Bhubaneswar", slug: "lyfe-bhubaneswar" },
  { name: "Lemon Tree Bhubaneswar", slug: "lemon-tree-bhubaneswar" },
  { name: "The World Backwaters", slug: "world-backwaters" },
];

export default function LogoStrip() {
  return (
    <section
      data-testid="trust-strip"
      aria-label="Trusted by hospitality leaders across India"
      className="relative border-y border-borderwarm bg-bone py-12 md:py-16 lg:py-20"
    >
      <div className="container-luxury">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Heading */}
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.32em] text-oxblood font-medium mb-3">
              Trusted By
            </div>
            <h3 className="font-serif text-[22px] md:text-[26px] lg:text-[28px] leading-[1.15] text-espresso tracking-tight text-balance">
              Hospitality leaders<br className="hidden md:block" /> across India
            </h3>
          </div>

          {/* Logo grid */}
          <div className="lg:col-span-9">
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8 md:gap-x-8 md:gap-y-10 items-center">
              {CLIENTS.map((c) => (
                <LogoCell key={c.slug} client={c} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function LogoCell({ client }) {
  const [errored, setErrored] = useState(false);
  return (
    <li
      data-testid={`trust-logo-${client.slug}`}
      className="group flex items-center justify-center h-14 md:h-16"
    >
      {!errored ? (
        <img
          src={`/logos/${client.slug}.png`}
          alt={client.name}
          loading="lazy"
          onError={() => setErrored(true)}
          className="max-h-full max-w-full object-contain transition-all duration-700 ease-out
                     grayscale opacity-60
                     group-hover:grayscale-0 group-hover:opacity-100"
        />
      ) : (
        <span className="font-serif italic text-[16px] md:text-[18px] text-espresso/55 group-hover:text-espresso transition-colors duration-500 text-center leading-tight">
          {client.name}
        </span>
      )}
    </li>
  );
}
